import type { ReactElement } from 'react';
import { useConnectGoogle } from '../api/source-queries';
import { Banner } from '../ui/Banner';
import { Button } from '../ui/Button';

export const GOOGLE_ERROR_TEXT: Record<'denied' | 'state' | 'scope' | 'no_refresh_token' | 'expired', string> = {
  denied: 'Google access was not granted.',
  state: 'That sign-in could not be verified. Try connecting again.',
  scope: 'ReportPad needs access to the files you pick. Try again and allow it.',
  no_refresh_token:
    'Google did not grant lasting access. Remove ReportPad in your Google account settings, then connect again.',
  expired: 'That sign-in took too long. Try connecting again.'
};

export type GoogleErrorCode = keyof typeof GOOGLE_ERROR_TEXT;

/** True for a code the callback can send; anything else is dropped. */
export function isGoogleErrorCode(value: unknown): value is GoogleErrorCode {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(GOOGLE_ERROR_TEXT, value);
}

/** Posts the return path, then sends the browser to Google's consent screen. */
export function ConnectGoogleButton({ returnTo, label = 'Connect Google' }: { returnTo: string; label?: string }): ReactElement {
  const connect = useConnectGoogle();
  return (
    <Button
      variant="secondary"
      busy={connect.isPending}
      onPress={() => {
        connect.mutate(
          { returnTo },
          {
            onSuccess: ({ url }) => {
              window.location.href = url;
            }
          }
        );
      }}
    >
      {label}
    </Button>
  );
}

/** Shows a callback's `?google_error=` code in words; nothing for none or an unknown code. */
export function GoogleErrorBanner({ code }: { code: string | undefined }): ReactElement | null {
  if (!isGoogleErrorCode(code)) return null;
  return <Banner tone="danger" title="Google did not connect">{GOOGLE_ERROR_TEXT[code]}</Banner>;
}
